import type { CSSProperties } from 'react'
import { Link } from '@tanstack/react-router'
import { Skeleton } from 'skullet/react'
import { create } from '@bufbuild/protobuf'

import type { Job } from '@/gen/cortado/v1/jobs_pb'
import { InvoiceLiteSchema, type InvoiceLite } from '@/gen/cortado/v1/invoices_pb'
import { BookingStageEnum, InvoiceStatusEnum, JobPaymentStateEnum, JobPhaseEnum, JobStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { useBooking } from '@/stores/bookings.store'
import { formatCurrency } from '@/lib/currency-utils'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { InvoiceStatusBadge } from '@/components/shared/enum-badge'
import { jobBookingTotals, jobDetailBookingStageTitle, type JobBookingTotals } from '@/components/pipeline/job-detail-view'

interface JobBookingProps {
  job: Job
  /** The job's invoices, fetched by the page (the stepper reads them too). */
  invoices: InvoiceLite[]
  isInvoicesLoading: boolean
  isInvoicesError: boolean
  /**
   * Renders as one column of the job page's combined Booking card instead of
   * a card of its own, titled Invoices.
   */
  embedded?: boolean
}

const SAMPLE_INVOICES: InvoiceLite[] = [
  { title: 'Deposit', total: 150000n, paid: 150000n, status: InvoiceStatusEnum.INVOICE_STATUS_PAID },
  { title: 'Final balance', total: 350000n, paid: 0n, status: InvoiceStatusEnum.INVOICE_STATUS_SENT },
].map(({ title, total, paid, status }, i) =>
  create(InvoiceLiteSchema, {
    id: `sample-${i}`,
    invoiceNumber: 1041 + i,
    title,
    totalCents: total,
    amountPaidCents: paid,
    amountDueCents: total - paid,
    status,
  }),
)

function money(cents: bigint): string {
  return `$${formatCurrency(Number(cents))}`
}

function Figure({ label, cents, className }: { label: string; cents: bigint; className?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('font-financial text-base font-medium', className)}>{money(cents)}</div>
    </div>
  )
}

/** The booking's invoices in invoice-number order, with cancelled ones sunk to the bottom. */
function sortByInvoiceNumber(invoices: InvoiceLite[]): InvoiceLite[] {
  return [...invoices].sort((a, b) => {
    const aCancelled = a.status === InvoiceStatusEnum.INVOICE_STATUS_CANCELLED
    const bCancelled = b.status === InvoiceStatusEnum.INVOICE_STATUS_CANCELLED
    if (aCancelled !== bCancelled) return aCancelled ? 1 : -1
    return a.invoiceNumber - b.invoiceNumber
  })
}

// Same overdue rule as the Booking step's "Payment overdue".
function isBookingOverdue(job: Job, totals: JobBookingTotals): boolean {
  return (
    totals.dueCents > 0n &&
    (totals.hasOverdue || job.paymentState === JobPaymentStateEnum.JOB_PAYMENT_STATE_OVERDUE)
  )
}

function InvoiceRow({ jobId, invoice }: { jobId: string; invoice: InvoiceLite }) {
  const pageParams = { id: jobId, invoiceId: invoice.id }
  const label = `Invoice #${invoice.invoiceNumber}`
  const isCancelled = invoice.status === InvoiceStatusEnum.INVOICE_STATUS_CANCELLED
  const isOverdue = invoice.status === InvoiceStatusEnum.INVOICE_STATUS_OVERDUE
  const isPartiallyPaid = !isCancelled && invoice.amountDueCents > 0n && invoice.amountPaidCents > 0n
  return (
    <li className="flex flex-col gap-1 px-4 py-3">
      <div className="flex items-center gap-2">
        <Link to="/pipeline/$id/invoices/$invoiceId" params={pageParams} className="text-sm font-medium hover:text-brand">
          {label}
        </Link>
        <InvoiceStatusBadge status={invoice.status} />
        <span
          className={cn(
            'ml-auto font-financial text-sm font-medium',
            isCancelled && 'text-muted-foreground line-through',
          )}
        >
          {money(invoice.totalCents)}
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span className="min-w-0 flex-1 truncate">{invoice.title}</span>
        {isPartiallyPaid && (
          <span className="shrink-0 font-financial">
            {money(invoice.amountPaidCents)} of {money(invoice.totalCents)} paid ·{' '}
            <span className={cn(isOverdue && 'text-destructive')}>{money(invoice.amountDueCents)} due</span>
          </span>
        )}
        <Link
          to="/pipeline/$id/invoices/$invoiceId"
          params={pageParams}
          aria-label={`Open ${label}`}
          className="shrink-0 hover:text-foreground"
        >
          Open →
        </Link>
      </div>
    </li>
  )
}

/** What was invoiced, paid and is still owed, over the invoices in invoice-number order. */
function BookingInvoices({ job, invoices }: { job: Job; invoices: InvoiceLite[] }) {
  const totals = jobBookingTotals(invoices)
  const paidPercent =
    totals.invoicedCents > 0n ? Math.min(100, Number((totals.paidCents * 100n) / totals.invoicedCents)) : 0
  const isOverdue = isBookingOverdue(job, totals)
  return (
    <>
      <section aria-label="Payments" className="flex flex-col gap-2">
        <div className="grid grid-cols-3 gap-3">
          <Figure label="Total" cents={totals.invoicedCents} />
          <Figure
            label="Paid"
            cents={totals.paidCents}
            className={cn(totals.paidCents > 0n && 'text-status-success-fg')}
          />
          <Figure
            label="Balance due"
            cents={totals.dueCents}
            className={cn(totals.dueCents > 0n && 'font-semibold', isOverdue && 'text-destructive')}
          />
        </div>
        <div
          role="progressbar"
          aria-label="Paid so far"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={paidPercent}
          className="h-1.5 overflow-hidden rounded-full bg-muted"
        >
          <div
            className={cn('h-full w-(--paid-percent) rounded-full', isOverdue ? 'bg-destructive' : 'bg-brand')}
            style={{ '--paid-percent': `${paidPercent}%` } as CSSProperties}
          />
        </div>
      </section>
      <ul aria-label="Invoices" className="divide-y rounded-lg border">
        {sortByInvoiceNumber(invoices).map((invoice) => (
          <InvoiceRow key={invoice.id} jobId={job.id} invoice={invoice} />
        ))}
      </ul>
    </>
  )
}

/**
 * Booking card on the pipeline job page, shown once a quote is accepted and
 * the job has a booking: where the money stands (the Booking step's sub-state),
 * what's been invoiced, paid and still owed, the booking's invoices in
 * invoice-number order. The booking questionnaire lives on the Logistics card
 * below, and booked events stay on the Events card. Hidden on a lost job, which shows its declined banner instead.
 */
export function JobBooking({ job, invoices, isInvoicesLoading, isInvoicesError, embedded = false }: JobBookingProps) {
  const { booking } = useBooking(job.bookingId)

  if (!job.bookingId || job.status === JobStatusEnum.JOB_STATUS_LOST) return null

  const totals = jobBookingTotals(invoices)
  const isOverdue = isBookingOverdue(job, totals)
  // The Booking step's sub-state (Awaiting deposit, Deposit received, …), once the invoices are in.
  const subState =
    isInvoicesLoading || isInvoicesError ? undefined : jobDetailBookingStageTitle(job, totals)
  const isCancelled = booking?.stage === BookingStageEnum.BOOKING_STAGE_CANCELLED

  const Root = embedded ? 'div' : Card

  return (
    <Root className={embedded ? 'flex min-w-0 flex-col gap-3' : 'gap-3 pt-4'}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">{embedded ? 'Invoices' : 'Booking'}</CardTitle>
          {isCancelled && <Badge variant="destructive">Cancelled</Badge>}
          {totals.invoiceCount > 1 && (
            <span className="ml-auto text-xs text-muted-foreground">{totals.invoiceCount} invoices</span>
          )}
        </div>
        {subState && (
          <p className={cn('text-sm', isOverdue ? 'text-destructive' : 'text-muted-foreground')}>{subState}</p>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pb-4">
        {isInvoicesError && !isInvoicesLoading ? (
          <p className="text-sm text-muted-foreground">Couldn&apos;t load invoices.</p>
        ) : (
          <Skeleton
            data={isInvoicesLoading ? null : invoices}
            sample={SAMPLE_INVOICES}
            label="Loading invoices"
            empty={!isInvoicesLoading && totals.invoiceCount === 0 && invoices.length === 0}
            emptyMessage={
              <p className="text-sm text-muted-foreground">
                {job.phase === JobPhaseEnum.JOB_PHASE_COMPLETE
                  ? 'No invoices were sent for this booking.'
                  : 'No invoices yet. Invoice the accepted quote to collect a deposit.'}
              </p>
            }
          >
            {(invoices) => <BookingInvoices job={job} invoices={invoices} />}
          </Skeleton>
        )}
      </CardContent>
    </Root>
  )
}
